/**
 * Keeps the browser's offer to install DAMS (DE-1707, ADR-0005).
 *
 * Chromium fires beforeinstallprompt once, often before React has mounted or the
 * user has logged in, so the app captures it at startup and offers it later.
 */

/**
 * Starts capturing the install offer into the shared store. Call once, before rendering.
 * @param {EventTarget} win  The window.
 */
export const captureInstallPrompt = (win) => installPromptStore.capture(win);

/**
 * Creates a store for the deferred beforeinstallprompt event.
 * @returns {{
 *   capture: function(EventTarget): void,
 *   promptEvent: function(): (Event|null),
 *   takePromptEvent: function(): (Event|null),
 *   isInstalled: function(): boolean,
 *   subscribe: function(function(): void): function(): void
 * }}
 */
export const createInstallPromptStore = () => {
    const listeners = new Set();
    let capturedEvent = null;
    let installed = false;
    let capturing = false;

    const notify = () => listeners.forEach((listener) => listener());

    const onBeforeInstallPrompt = (event) => {
        // Stops the browser's own mini-infobar; DAMS offers installation itself.
        event.preventDefault();
        capturedEvent = event;
        notify();
    };

    const onAppInstalled = () => {
        capturedEvent = null;
        installed = true;
        notify();
    };

    return {
        capture: (win) => {
            if (capturing) {
                return;
            }
            capturing = true;
            win.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
            win.addEventListener('appinstalled', onAppInstalled);
        },
        promptEvent: () => capturedEvent,
        // A page may call prompt() on an event only once.
        takePromptEvent: () => {
            const event = capturedEvent;
            capturedEvent = null;
            if (event) {
                notify();
            }
            return event;
        },
        isInstalled: () => installed,
        subscribe: (listener) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
    };
};

/** The store shared by the app. */
export const installPromptStore = createInstallPromptStore();
